import React, { useState, useEffect } from 'react';
import { getRoles, updateRoleActions, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { KernelPage } from '../kernelAdminShared';

const groupOf = (code) => code.split('.')[0];

function RolePermissions({ user, onLogout }) {
  const { t } = useLanguage();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState({}); // roleId -> Set(codes)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingRole, setSavingRole] = useState(null);

  const fetchRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRoles();
      setRoles(data.roles || []);
      setCatalog(data.catalog || []);
      const next = {};
      (data.roles || []).forEach((r) => {
        next[r.id] = new Set(r.actions || []);
      });
      setSelected(next);
    } catch (err) {
      setError(errorMessage(err, t('roles.errorLoad')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (roleId, code) => {
    setSelected((prev) => {
      const set = new Set(prev[roleId] || []);
      if (set.has(code)) {
        set.delete(code);
      } else {
        set.add(code);
      }
      return { ...prev, [roleId]: set };
    });
  };

  const toggleGroup = (roleId, group, enabled) => {
    setSelected((prev) => {
      const set = new Set(prev[roleId] || []);
      catalog
        .filter((item) => groupOf(item.code) === group)
        .forEach((item) => {
          if (enabled) {
            set.add(item.code);
          } else {
            set.delete(item.code);
          }
        });
      return { ...prev, [roleId]: set };
    });
  };

  const groupState = (roleId, group) => {
    const set = selected[roleId] || new Set();
    const codes = catalog.filter((item) => groupOf(item.code) === group).map((item) => item.code);
    const checked = codes.filter((code) => set.has(code)).length;
    return { total: codes.length, checked, all: codes.length > 0 && checked === codes.length };
  };

  const saveRole = async (role) => {
    setError('');
    setSuccess('');
    setSavingRole(role.id);
    try {
      const actions = Array.from(selected[role.id] || []).sort();
      await updateRoleActions(role.id, actions);
      setSuccess(t('roles.saved', { name: role.name }));
    } catch (err) {
      setError(errorMessage(err, t('roles.errorSave')));
    } finally {
      setSavingRole(null);
    }
  };

  const groups = [...new Set(catalog.map((item) => groupOf(item.code)))];

  return (
    <KernelPage user={user} onLogout={onLogout}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('roles.title')}</h1>
          <p className="page-subtitle">{t('roles.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && <div className="loading">{t('common.loading')}</div>}

      {!loading &&
        roles.map((role) => {
          const roleSet = selected[role.id] || new Set();
          return (
            <div className="card" key={role.id}>
              <div className="card-header">
                <h3>
                  {role.name}
                  {role.description && <span className="muted"> — {role.description}</span>}
                </h3>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={savingRole === role.id}
                  onClick={() => saveRole(role)}
                >
                  {savingRole === role.id ? t('common.saving') : t('common.save')}
                </button>
              </div>
              <div className="card-body">
                <div className="perm-groups">
                  {groups.map((group) => {
                    const items = catalog.filter((item) => groupOf(item.code) === group);
                    const st = groupState(role.id, group);
                    return (
                      <div className="perm-group" key={group}>
                        <div className="perm-group-header">
                          <strong>{t(`roles.group.${group}`)}</strong>
                          <span className="perm-group-count">
                            {st.checked}/{st.total}
                          </span>
                          <label className="form-row-check perm-group-toggle">
                            <input
                              type="checkbox"
                              checked={st.all}
                              onChange={(e) => toggleGroup(role.id, group, e.target.checked)}
                            />
                            <span>{t('roles.toggleAll')}</span>
                          </label>
                        </div>
                        <div className="perm-items">
                          {items.map((item) => (
                            <label
                              className={`perm-item${roleSet.has(item.code) ? ' is-checked' : ''}`}
                              key={item.code}
                            >
                              <input
                                type="checkbox"
                                checked={roleSet.has(item.code)}
                                onChange={() => toggle(role.id, item.code)}
                              />
                              <span className="perm-code">{item.code}</span>
                              <span className="perm-desc">{item.description}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
    </KernelPage>
  );
}

export default RolePermissions;
